import React, { useMemo } from 'react';
import styled from 'styled-components';
import * as R from 'ramda';
import { Pie } from 'react-chartjs-2';
// import 'chartjs-plugin-labels';

import { colors, neumorphism } from '../constant';
import { secondToHM, hexToRgb, numberWithCommas } from '../methods';
import { respondMore, respondTo } from '../responsive';

const pieOptions = isCostPie => ({ 
    aspectRatio: 1, 
    maintainAspectRatio: false, 
    legend: { display: false },
    elements: {
        arc: {
            borderWidth: 0
        }
    },
    tooltips: {
        callbacks: {
            label: function(tooltipItem, data) {
                var label = data.labels[tooltipItem.index] || '';

                if (label) { label += ': ';}

                label += isCostPie ? 
                    `$${numberWithCommas(Math.round(data.datasets[0]?.data[tooltipItem.index] || 0))}`:
                    secondToHM(data.datasets[0]?.data[tooltipItem.index]) || '-';

                return label;
            }
        }
    },
    // plugins: {
    //     labels: {
    //         render: 'label',
    //         fontColor: function (data) {
    //             var rgb = hexToRgb(data.dataset.backgroundColor[data.index]);
    //             var threshold = 140;
    //             var luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
    //             return luminance > threshold ? colors.label1 : 'white';
    //         },
    //     }
    // }
})

const PieBlock = ({ label, data, total, isCostPie = false }) => {

    const postData = useMemo(() => {
        data = isCostPie ? 
            R.sort((a, b) => b.cost - a.cost, data): 
            R.sort((a, b) => b.used - a.used, data);

        let chartLabels = R.pluck('name', data);
        let chartData = isCostPie ? R.pluck('cost', data) : R.pluck('used', data);
        let chartBgColor = data.map((_, i) => lerpColor(colors.primary1, colors.primary2, i / (data.length - 1)));
        
        let _total = total;
        const dataSum = R.sum(chartData);
        
        if (total !== undefined && total > dataSum) {
            chartLabels = [ '剩餘工時', ...chartLabels ];
            chartData = [ (total - dataSum) , ...chartData];
            chartBgColor = [ '#E2E2E2', ...chartBgColor ]
        }
        else {
            _total = dataSum;
        }

        return { chartLabels, chartData, chartBgColor, _total, dataSum };
    }, [data])

    const { chartLabels, chartData, chartBgColor, _total, dataSum } = postData;

    return (
        <Root>
            <PieContainer>
                <Pie 
                    data={{
                        labels: chartLabels,
                        datasets: [{ data: chartData, backgroundColor: chartBgColor }],
                    }} 
                    options={pieOptions(isCostPie)}
                />
            </PieContainer>
            <InfoContainer>
                <h2>{ label }</h2>
                { data?.map((item, index) =>
                    <InfoItem key={index}>
                        <span>{ item.name }</span>
                        { !isCostPie && <span>{ ~~(item.used / _total * 10000) / 100 }%</span> }
                        { isCostPie && <span>{ ~~(item.cost / _total * 10000) / 100 }%</span> }
                        <span>{ secondToHM(item.used) }</span>
                        { isCostPie && <span>$ { numberWithCommas(Math.round(item.cost)) }</span> }
                    </InfoItem>
                )}
                <FillBeforeSum/>
                <SumItem>
                    <span>目前總計</span>
                    { !isCostPie && <>
                        <span>{ ~~(dataSum / _total * 10000) / 100 }%</span>
                        <span>{ secondToHM(dataSum) }</span> 
                    </>}
                    { isCostPie && <>
                        <span/><span/>
                        <span>$ { numberWithCommas(Math.round(dataSum)) }</span> 
                    </>}
                </SumItem>
            </InfoContainer>
        </Root>
    )
}

const Root = styled.div`
    display: flex;
    flex: 1;

    ${ respondTo.md } {
        flex-direction: column;
    }
`

const PieContainer = styled.div`
    position: relative;
    width: 220px;
    height: 220px;
    border-radius: 500em;
    ${ respondMore.md } {
        ${ neumorphism.convex };
    }

    ${ respondTo.md } {
        margin-bottom: 16px;
        width: 100%;
        height: 160px;
    }
`

const InfoContainer = styled.div`
    flex: 1;
    margin-left: 40px;
    
    display: flex;
    flex-direction: column;

    >h2 {
        color: ${ colors.label2 };
        font-size: 12px;
        font-weight: 400;
        margin: 0;
        margin-bottom: 16px;
    }

    ${ respondTo.md } {
        margin-left: 0;
    }
`

const FillBeforeSum = styled.div`
    flex: 1;
`

const InfoItem = styled.div`
    display: flex;
    align-content: center;
    margin-bottom: 12px;

    >span {
        color: ${ colors.label1 };
        font-size: 14px;
    }

    >span:nth-child(1) {
        display: inline-block;
        width: 80px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    >span:nth-child(2) {
        font-size: 18px;
        font-weight: bold;
        color: ${ colors.text };
        flex: 1;
    }

    >span:nth-child(4) {
        min-width: 120px;
        font-size: 16px;
        /* font-weight: bold; */
        color: ${ colors.text };
        text-align: right;
    }
`

const SumItem = styled(InfoItem)`
    padding-top: 16px;
    border-top: 1px solid #E0E0E0;
    margin-top: 24px;
    margin-bottom: 0;
`

const lerpColor = (a, b, amount) => { 
    if (!amount) amount = 0;
    var ah = parseInt(a.replace(/#/g, ''), 16),
        ar = ah >> 16, ag = ah >> 8 & 0xff, ab = ah & 0xff,
        bh = parseInt(b.replace(/#/g, ''), 16),
        br = bh >> 16, bg = bh >> 8 & 0xff, bb = bh & 0xff,
        rr = ar + amount * (br - ar),
        rg = ag + amount * (bg - ag),
        rb = ab + amount * (bb - ab);

    return '#' + ((1 << 24) + (rr << 16) + (rg << 8) + rb | 0).toString(16).slice(1);
}

export default PieBlock;
